import React, { useState, useEffect } from 'react';
import { userAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Users, Plus, Copy, LogOut, Trophy, Crown, UserPlus } from 'lucide-react';
import { InviteDialog } from '../../components/InviteDialog';
import { groupInviteLink, referralLink, myReferralCode } from '../../lib/share';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '../../lib/apiErrors';
import { motion } from 'framer-motion';

export function GroupsPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const { t } = useTranslation();
  const { user } = useAuth() || {};
  const [invite, setInvite] = useState(null); // {title, description, text, link}

  const openGroupInvite = (group) => {
    const code = group.code || group.join_code;
    setInvite({
      title: t('invite.groupTitle', { name: group.name }),
      description: t('invite.groupDesc', { code }),
      text: t('invite.groupText', { name: group.name, code }),
      link: groupInviteLink(code),
    });
  };

  const openFriendInvite = async () => {
    const code = await myReferralCode(user, userAPI.getReferralStats);
    setInvite({
      title: t('invite.friendTitle'),
      description: t('invite.friendDesc'),
      text: t('share.inviteText'),
      link: referralLink(code),
    });
  };

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      const res = await userAPI.getMyGroups();
      setGroups(res.data.groups || []);
    } catch (error) {
      toast.error(t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    
    setCreating(true);
    try {
      await userAPI.createGroup(newGroupName.trim());
      toast.success(t('groups.created'));
      setShowCreateDialog(false);
      setNewGroupName('');
      loadGroups();
    } catch (error) {
      toast.error(apiErrorMessage(error, t, 'errors.createFailed'));
    } finally {
      setCreating(false);
    }
  };

  const handleJoinGroup = async () => {
    if (!joinCode.trim()) return;
    
    setJoining(true);
    try {
      await userAPI.joinGroup(joinCode.trim().toUpperCase());
      toast.success(t('groups.joined'));
      setShowJoinDialog(false);
      setJoinCode('');
      loadGroups();
    } catch (error) {
      toast.error(apiErrorMessage(error, t, 'errors.joinFailed'));
    } finally {
      setJoining(false);
    }
  };

  const handleLeaveGroup = async (groupId) => {
    if (!window.confirm(t('groups.confirmLeave'))) return;
    
    try {
      await userAPI.leaveGroup(groupId);
      toast.success(t('groups.left'));
      loadGroups();
    } catch (error) {
      toast.error(apiErrorMessage(error, t, 'errors.leaveFailed'));
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(t('groups.codeCopied'));
    } catch (e) {
      toast.info(code); // clipboard blocked (some in-app browsers): show the code instead
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl font-bold text-white mb-2 flex items-center gap-3"
          >
            <Users className="w-8 h-8 text-teal-400" />
            {t('groups.title')}
          </motion.h1>
          <p className="text-slate-400">{t('groups.subtitle')}</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <Button
          onClick={() => setShowCreateDialog(true)}
          className="flex-1 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('groups.create')}
        </Button>
        <Button
          onClick={() => setShowJoinDialog(true)}
          variant="outline"
          className="flex-1 bg-transparent border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white"
        >
          <Users className="w-4 h-4 mr-2" />
          {t('groups.join')}
        </Button>
      </div>
      <Button
        onClick={openFriendInvite}
        variant="outline"
        className="w-full bg-teal-500/10 border-teal-500/30 text-teal-200 hover:bg-teal-500/20 hover:text-white"
        data-testid="invite-friend-btn"
      >
        <UserPlus className="w-4 h-4 mr-2" />
        {t('invite.friend')}
      </Button>

      {/* Groups List */}
      {groups.length === 0 ? (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-slate-800/50 rounded-2xl p-12 text-center border border-slate-700/50"
        >
          <Users className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">{t('groups.noGroups')}</h3>
          <p className="text-slate-400 mb-6">{t('groups.noGroupsDesc')}</p>
        </motion.div>
      ) : (
        <div className="space-y-4">
          {groups.map((group, idx) => (
            <motion.div
              key={group._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    {group.name}
                    {group.is_owner && <Crown className="w-5 h-5 text-yellow-400" />}
                  </h3>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="text-sm text-slate-400 flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {t('groups.memberCount', { count: group.member_count || group.members?.length || 0 })}
                    </span>
                    <button
                      onClick={() => copyCode(group.code || group.join_code)}
                      className="text-sm text-teal-400 hover:text-teal-300 flex items-center gap-1"
                    >
                      <Copy className="w-4 h-4" />
                      {group.code || group.join_code}
                    </button>
                  </div>
                </div>
                
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button
                    onClick={() => openGroupInvite(group)}
                    size="sm"
                    className="bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold rounded-lg"
                    data-testid={`invite-group-btn-${idx}`}
                  >
                    <UserPlus className="w-4 h-4 mr-1.5" />
                    {t('invite.button')}
                  </Button>
                  <Button
                    onClick={() => handleLeaveGroup(group._id)}
                    variant="ghost"
                    size="sm"
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    aria-label={t('groups.leave', 'Leave group')}
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Member Nicknames */}
              {group.member_nicknames && group.member_nicknames.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-slate-500 mb-2">{t('groups.membersList')}:</p>
                  <div className="flex flex-wrap gap-2">
                    {group.member_nicknames.map((nickname, i) => (
                      <span key={i} className="px-3 py-1 bg-slate-700/50 rounded-full text-sm text-slate-300">
                        {nickname}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Group Rankings Preview */}
              {group.top_members && group.top_members.length > 0 && (
                <div className="border-t border-slate-700 pt-4">
                  <p className="text-sm text-slate-400 mb-3 flex items-center gap-2">
                    <Trophy className="w-4 h-4" />
                    {t('groups.topPlayers')}
                  </p>
                  <div className="space-y-2">
                    {group.top_members.slice(0, 3).map((member, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="text-slate-300">
                          {i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'} {member.nickname}
                        </span>
                        <span className="text-teal-400 font-medium">
                          {member.total_correct}/{member.total_questions}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {/* Create Group Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>{t('groups.createNew')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Input
              placeholder={t('groups.namePlaceholder')}
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              className="bg-slate-700 border-slate-600 text-white"
            />
            <Button
              onClick={handleCreateGroup}
              disabled={creating || !newGroupName.trim()}
              className="w-full bg-gradient-to-r from-teal-500 to-cyan-500"
            >
              {creating ? t('common.creating') : t('groups.create')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <InviteDialog
        open={!!invite}
        onOpenChange={(open) => { if (!open) setInvite(null); }}
        title={invite?.title}
        description={invite?.description}
        text={invite?.text || ''}
        link={invite?.link || ''}
      />

      {/* Join Group Dialog */}
      <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>{t('groups.joinExisting')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Input
              placeholder={t('groups.codePlaceholder')}
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              className="bg-slate-700 border-slate-600 text-white font-mono text-center text-lg tracking-widest"
              maxLength={8}
            />
            <Button
              onClick={handleJoinGroup}
              disabled={joining || !joinCode.trim()}
              className="w-full bg-gradient-to-r from-teal-500 to-cyan-500"
            >
              {joining ? t('common.joining') : t('groups.join')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
